import { useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Plus, Trash2, UserCircle2, Users } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// Maps UI relationship → Supabase family_profiles.type
const RELATIONSHIPS = [
  { value: "self", label: "Myself" },
  { value: "child", label: "Child" },
  { value: "spouse", label: "Partner / Spouse" },
  { value: "parent", label: "Parent" },
  { value: "other", label: "Other" },
];

const COLOR_STYLES = [
  "bg-primary text-primary-foreground",
  "bg-accent text-accent-foreground",
  "bg-rose-500 text-white",
  "bg-indigo-500 text-white",
  "bg-amber-500 text-white",
];

function colorForName(name) {
  if (!name) return COLOR_STYLES[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) >>> 0;
  return COLOR_STYLES[h % COLOR_STYLES.length];
}

export default function Profiles() {
  const { profiles, refreshProfiles } = useOutletContext();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  const [error, setError] = useState("");

  function blank() {
    return {
      name: "",
      type: "child",
      birthdate: "",
      blood_type: "",
      allergies: "",
    };
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    try {
      const { data: userData } = await supabase.auth.getUser();
      const user_id = userData?.user?.id;
      const payload = {
        user_id,
        name: form.name,
        type: form.type,
        birthdate: form.birthdate || null,
        blood_type: form.blood_type || null,
        allergies: form.allergies
          ? form.allergies.split(",").map((s) => s.trim()).filter(Boolean)
          : [],
      };
      const { error } = await supabase.from("family_profiles").insert(payload);
      if (error) throw new Error(error.message);
      setForm(blank());
      setOpen(false);
      refreshProfiles();
    } catch (err) {
      setError(err.message);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this profile? Records linked by ID will also be hidden from this app.")) return;
    const { error } = await supabase.from("family_profiles").delete().eq("id", id);
    if (!error) refreshProfiles();
  };

  return (
    <div data-testid="profiles-page" className="space-y-8">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-accent">
            Family
          </span>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight">
            Profiles
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Add everyone you care for — from toddlers to aging parents.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="lg" data-testid="add-profile-btn">
              <Plus className="mr-1 h-4 w-4" /> Add profile
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>New family profile</DialogTitle>
            </DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Name *</Label>
                  <Input
                    data-testid="profile-name"
                    required
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="e.g. Emma"
                  />
                </div>
                <div>
                  <Label>Relationship</Label>
                  <Select
                    value={form.type}
                    onValueChange={(v) => setForm({ ...form, type: v })}
                  >
                    <SelectTrigger data-testid="profile-relationship">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {RELATIONSHIPS.map((r) => (
                        <SelectItem key={r.value} value={r.value}>
                          {r.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Date of birth</Label>
                  <Input
                    type="date"
                    value={form.birthdate}
                    onChange={(e) => setForm({ ...form, birthdate: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Blood type</Label>
                  <Input
                    value={form.blood_type}
                    onChange={(e) => setForm({ ...form, blood_type: e.target.value })}
                    placeholder="e.g. O+"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Allergies (comma separated)</Label>
                  <Input
                    value={form.allergies}
                    onChange={(e) => setForm({ ...form, allergies: e.target.value })}
                    placeholder="peanuts, penicillin"
                  />
                </div>
              </div>
              {error && (
                <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                  {error}
                </div>
              )}
              <DialogFooter>
                <Button type="submit" data-testid="profile-submit">
                  Save profile
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {profiles.length === 0 ? (
        <EmptyState onAdd={() => setOpen(true)} />
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {profiles.map((p) => (
            <div
              key={p.id}
              className="group relative rounded-2xl bg-card p-6 shadow-soft transition-shadow hover:shadow-card"
              data-testid={`profile-card-${p.id}`}
            >
              <div
                className={`flex h-14 w-14 items-center justify-center rounded-2xl ${colorForName(p.name)} font-heading text-xl font-bold`}
              >
                {p.name?.[0]?.toUpperCase() || "?"}
              </div>
              <h3 className="mt-4 font-heading text-lg font-bold">{p.name}</h3>
              <p className="text-xs uppercase tracking-wider text-muted-foreground">
                {RELATIONSHIPS.find((r) => r.value === p.type)?.label || p.type}
              </p>
              <div className="mt-4 space-y-1 text-sm text-muted-foreground">
                {p.birthdate && (
                  <div>
                    <span className="text-foreground">DOB:</span> {p.birthdate}
                  </div>
                )}
                {p.blood_type && (
                  <div>
                    <span className="text-foreground">Blood:</span> {p.blood_type}
                  </div>
                )}
                {p.allergies?.length > 0 && (
                  <div>
                    <span className="text-foreground">Allergies:</span>{" "}
                    {p.allergies.join(", ")}
                  </div>
                )}
              </div>
              <button
                onClick={() => remove(p.id)}
                className="absolute right-3 top-3 rounded-lg p-2 text-muted-foreground opacity-0 transition-opacity hover:bg-destructive/10 hover:text-destructive group-hover:opacity-100"
                data-testid={`profile-delete-${p.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div
      className="flex flex-col items-center justify-center rounded-3xl border-2 border-dashed border-border bg-card/50 p-16 text-center"
      data-testid="profiles-empty"
    >
      <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-secondary">
        <Users className="h-8 w-8 text-primary" />
      </div>
      <h3 className="mt-4 font-heading text-xl font-bold">No profiles yet</h3>
      <p className="mt-2 max-w-sm text-sm text-muted-foreground">
        Add your first family member to start tracking their health.
      </p>
      <Button onClick={onAdd} className="mt-6" data-testid="profiles-empty-add">
        <UserCircle2 className="mr-1 h-4 w-4" /> Add your first profile
      </Button>
    </div>
  );
}
